import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { JobHttpService } from 'cms/features/feature_job/service';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Alert,
    Box,
    CircularProgress
} from '@mui/material';

function EmployeeForm({ open, onClose, employeeId, companyId, onSaved }) {
    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingEmployee, setLoadingEmployee] = useState(false);

    useEffect(() => {
        if (open) {
            if (employeeId) {
                loadEmployee();
            } else {
                reset({
                    employee_number: '',
                    first_name: '',
                    last_name: '',
                    role: '',
                    email: '',
                    address: '',
                    postal_code: '',
                    city: '',
                    phone_number: '',
                    account_number: ''
                });
                setError('');
            }
        }
    }, [open, employeeId, reset]);

    const loadEmployee = async () => {
        if (!employeeId) return;
        
        setLoadingEmployee(true);
        setError('');
        try {
            const employee = await JobHttpService.loadEmployee(employeeId);
            reset({
                employee_number: employee.employee_number || '',
                first_name: employee.first_name || '',
                last_name: employee.last_name || '',
                role: employee.role || '',
                email: employee.email || '',
                address: employee.address || '',
                postal_code: employee.postal_code || '',
                city: employee.city || '',
                phone_number: employee.phone_number || '',
                account_number: employee.account_number || ''
            });
        } catch (err) {
            console.error('Error loading employee:', err);
            setError(err?.message || 'Failed to load employee');
        } finally {
            setLoadingEmployee(false);
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);
        setError('');

        try {
            const submitData = {
                ...data,
                company: companyId
            };

            let response;
            if (employeeId) {
                response = await JobHttpService.updateEmployee(employeeId, submitData);
            } else {
                response = await JobHttpService.createEmployee(submitData);
            }

            if (response && !response.detail && !response.error) {
                if (onSaved) {
                    onSaved();
                }
                onClose();
            } else {
                setError(response?.detail || response?.error || 'Failed to save employee');
            }
        } catch (err) {
            console.error('Error saving employee:', err);
            setError(err?.message || 'Failed to save employee. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogTitle>
                    {employeeId ? 'Edit Employee' : 'Add New Employee'}
                </DialogTitle>
                <DialogContent>
                    {loadingEmployee && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {!loadingEmployee && (
                        <>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}
                            <Grid container spacing={2} sx={{ mt: 1 }}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Employee Number"
                                        {...register("employee_number")}
                                        error={Boolean(errors.employee_number)}
                                        helperText={errors.employee_number?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Role"
                                        {...register("role")}
                                        error={Boolean(errors.role)}
                                        helperText={errors.role?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        {...register("first_name", {
                                            required: "First name is required"
                                        })}
                                        error={Boolean(errors.first_name)}
                                        helperText={errors.first_name?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        {...register("last_name", {
                                            required: "Last name is required"
                                        })}
                                        error={Boolean(errors.last_name)}
                                        helperText={errors.last_name?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        {...register("email")}
                                        error={Boolean(errors.email)}
                                        helperText={errors.email?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        {...register("phone_number")}
                                        error={Boolean(errors.phone_number)}
                                        helperText={errors.phone_number?.message}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Address"
                                        {...register("address")}
                                        error={Boolean(errors.address)}
                                        helperText={errors.address?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Postal Code"
                                        {...register("postal_code")}
                                        error={Boolean(errors.postal_code)}
                                        helperText={errors.postal_code?.message}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="City"
                                        {...register("city")}
                                        error={Boolean(errors.city)}
                                        helperText={errors.city?.message}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Account Number"
                                        {...register("account_number")}
                                        error={Boolean(errors.account_number)}
                                        helperText={errors.account_number?.message}
                                        placeholder="Bank account number for salary payment"
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancel
                    </Button>
                    <Button 
                        type="submit" 
                        variant="contained" 
                        disabled={loading || loadingEmployee}
                    >
                        {loading ? <CircularProgress size={20} /> : (employeeId ? 'Update' : 'Create')}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export default EmployeeForm;
